import { useState } from 'react';
import {
  Button,
  CompactTriggerButton,
  DateTimeField,
  KanbanTag,
  Modal,
  ModalActions,
} from '@evogard/ui';

/**
 * Os três instantes que os selos de prazo leem — o resumo do sinistro e o do
 * acionamento de Periféricos (15/10/2026) têm os três.
 */
export interface DeadlineFacts {
  phaseEnteredAt: string | null;
  phaseDeadlineAt: string | null;
  dueAt: string | null;
}
import {
  claimDueState,
  formatSpan,
  phaseSlaState,
  type ClaimDepartment,
} from '@evogard/domain';
import { useDeadlinePermissions, useSetDueDate } from './useDeadlines.js';

/** "da Análise", "do Reparo" — a preposição vai com o nome. */
const DEPARTMENT_OF: Readonly<Record<ClaimDepartment, string>> = {
  // @valor — as chaves são os valores de `ClaimDepartment`
  analise: 'da Análise',
  reparo: 'do Reparo',
  indenizacao: 'da Indenização',
};
/**
 * Os dois relógios na tela (07/10/2026): o SLA da fase, com o relógio, e o
 * vencimento do card no departamento, com o calendário. Cada um com a sua
 * palavra — o SLA EXPIRA, o card VENCE — e as três cores da casa: discreto no
 * prazo, alerta perto, erro estourado. A regra de estado é a do domínio
 * (`claims-deadlines.ts`), a mesma do filtro do quadro.
 */

type Tone = 'neutro' | 'alerta' | 'erro';

const DAY_MONTH = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });
const HOUR_MINUTE = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
});

/** "hoje, 18:00", "amanhã, 09:00" ou "15/10". */
function whenOf(date: Date, now: Date): string {
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((day(date) - day(now)) / 86_400_000);
  if (diff === 0) return `hoje, ${HOUR_MINUTE.format(date)}`;
  if (diff === 1) return `amanhã, ${HOUR_MINUTE.format(date)}`;
  return DAY_MONTH.format(date);
}

/** O vencimento por extenso: "Vence 15/10", "Vence hoje, 18:00", "Vencido há 2 dias". */
export function dueLabel(
  dueAt: string,
  now = new Date(),
): { text: string; tone: Tone } | null {
  const state = claimDueState(dueAt, now);
  if (!state) return null;
  const date = new Date(dueAt);
  if (state === 'vencido') {
    return {
      text: `Vencido há ${formatSpan(now.getTime() - date.getTime())}`,
      tone: 'erro',
    };
  }
  return {
    text: `Vence ${whenOf(date, now)}`,
    tone: state === 'vence_em_breve' ? 'alerta' : 'neutro',
  };
}

/** O SLA por extenso: "18h na fase", "Expira em 4h", "SLA expirado há 3h". */
export function slaLabel(
  enteredAt: string,
  deadlineAt: string,
  now = new Date(),
): { text: string; tone: Tone } | null {
  const state = phaseSlaState(enteredAt, deadlineAt, now);
  if (!state) return null;
  const end = new Date(deadlineAt).getTime();
  if (state === 'expirado') {
    return { text: `SLA expirado há ${formatSpan(now.getTime() - end)}`, tone: 'erro' };
  }
  if (state === 'expira_em_breve') {
    return { text: `Expira em ${formatSpan(end - now.getTime())}`, tone: 'alerta' };
  }
  return {
    text: `${formatSpan(now.getTime() - new Date(enteredAt).getTime())} na fase`,
    tone: 'neutro',
  };
}

/**
 * Os selos de prazo de um card — o SLA primeiro, que é o que pede ação hoje.
 * Sem prazo nenhum, nada: ausência de prazo não ganha selo.
 */
export function ClaimDeadlineTags({ claim }: { claim: DeadlineFacts }) {
  const now = new Date();
  const sla =
    claim.phaseEnteredAt && claim.phaseDeadlineAt
      ? slaLabel(claim.phaseEnteredAt, claim.phaseDeadlineAt, now)
      : null;
  const due = claim.dueAt ? dueLabel(claim.dueAt, now) : null;
  if (!sla && !due) return null;
  return (
    <>
      {sla && (
        <KanbanTag icon="relogio" tone={sla.tone} title="SLA da fase">
          {sla.text}
        </KanbanTag>
      )}
      {due && claim.dueAt && <DueTag dueAt={claim.dueAt} />}
    </>
  );
}

/** O selo do vencimento de um card — o do sinistro e o do acionamento de Periféricos. */
export function DueTag({ dueAt }: { dueAt: string }) {
  const due = dueLabel(dueAt);
  if (!due) return null;
  return (
    <KanbanTag
      icon="calendario"
      tone={due.tone}
      title={`Vencimento em ${new Date(dueAt).toLocaleString('pt-BR')}`}
    >
      {due.text}
    </KanbanTag>
  );
}

/**
 * A linha do SLA abaixo da trilha da mesa: o selo e o tempo usado — "51h de
 * 48h em Documentação". Fase sem SLA não desenha nada.
 */
export function PhaseSlaLine({
  claim,
  phaseName,
}: {
  claim: DeadlineFacts;
  phaseName: string;
}) {
  if (!claim.phaseEnteredAt || !claim.phaseDeadlineAt) return null;
  const now = new Date();
  const label = slaLabel(claim.phaseEnteredAt, claim.phaseDeadlineAt, now);
  if (!label) return null;
  const start = new Date(claim.phaseEnteredAt).getTime();
  const total = Math.max(1, new Date(claim.phaseDeadlineAt).getTime() - start);
  const used = now.getTime() - start;
  const hours = (ms: number) => Math.floor(ms / 3_600_000);
  const percent = Math.min(100, Math.round((used / total) * 100));
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 pb-3 text-xs text-texto-suave">
      <KanbanTag icon="relogio" tone={label.tone}>
        {label.text}
      </KanbanTag>
      <div
        role="progressbar"
        aria-label={`SLA de ${phaseName}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="h-1.5 w-40 overflow-hidden rounded-pilula bg-superficie-suave"
      >
        <div
          className={
            label.tone === 'erro'
              ? 'h-full bg-erro'
              : label.tone === 'alerta'
                ? 'h-full bg-alerta'
                : 'h-full bg-progresso'
          }
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="tabular-nums">
        {hours(used)}h de {hours(total)}h em {phaseName}
      </span>
    </div>
  );
}

/** `Date` → `AAAA-MM-DDTHH:MM` no fuso de quem usa — o valor do `DateTimeField`. */
function localValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * O vencimento do card abaixo do nome na mesa, ao lado do analista: a pastilha
 * de calendário com o texto do estado. Sem vencimento, a mesma pastilha,
 * tracejada (08/10/2026). Clicar abre o diálogo de data e hora; "Tirar" fica nele.
 *
 * O vencimento é do DEPARTAMENTO onde o card está — o diálogo diz qual, e a
 * API recusa (409) se o card mudou de departamento com a mesa aberta.
 */
export function DueDatePicker({
  claimId,
  department,
  dueAt,
  enabled,
}: {
  claimId: number;
  department: ClaimDepartment;
  dueAt: string | null;
  /** Falso no sinistro arquivado. */
  enabled: boolean;
}) {
  const save = useSetDueDate(claimId);
  return (
    <DueDateControl
      of={DEPARTMENT_OF[department]}
      dueAt={dueAt}
      enabled={enabled}
      save={{
        mutate: (next, options) => save.mutate({ department, dueAt: next }, options),
        reset: save.reset,
        isPending: save.isPending,
        error: save.error,
      }}
    />
  );
}

/** Gravar o vencimento — o do sinistro no departamento, ou o do acionamento de Periféricos. */
export interface SaveDueDate {
  mutate(dueAt: string | null, options?: { onSuccess?: () => void }): void;
  reset(): void;
  isPending: boolean;
  error: Error | null;
}

/**
 * A pastilha e o diálogo do vencimento, sem saber de quem é o card. `of` é o
 * complemento do título: "da Análise", "de Periféricos".
 */
export function DueDateControl({
  of,
  dueAt,
  enabled,
  save,
}: {
  of: string;
  dueAt: string | null;
  enabled: boolean;
  save: SaveDueDate;
}) {
  const { canSetDueDate } = useDeadlinePermissions();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const label = dueAt ? dueLabel(dueAt) : null;
  const title = label ? `${label.text} — vencimento ${of}` : `Definir o vencimento ${of}`;
  const editable = enabled && canSetDueDate;

  if (!editable && !dueAt) return null;

  return (
    <>
      <CompactTriggerButton
        compact={{
          icon: 'calendario',
          filled: false,
          text: label?.text,
          title,
          loading: save.isPending,
          tone: label?.tone,
        }}
        disabled={!editable}
        onClick={() => {
          setValue(dueAt ? localValue(dueAt) : '');
          save.reset();
          setOpen(true);
        }}
      />
      {open && (
        <Modal
          open
          onOpenChange={(o) => !o && setOpen(false)}
          title={`Vencimento ${of}`}
          descricao="A data-limite do card neste departamento. O SLA das fases é outro relógio, e não muda com ela."
          size="sm"
        >
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!value) return;
              save.mutate(new Date(value).toISOString(), {
                onSuccess: () => setOpen(false),
              });
            }}
          >
            <DateTimeField
              label="Vence em"
              required
              value={value}
              onChange={setValue}
              layer="modal"
              error={save.error?.message}
            />
            <ModalActions>
              {dueAt ? (
                <Button
                  type="button"
                  variant="secundario"
                  disabled={save.isPending}
                  onClick={() => save.mutate(null, { onSuccess: () => setOpen(false) })}
                >
                  Tirar vencimento
                </Button>
              ) : (
                <Button type="button" variant="secundario" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
              )}
              <Button type="submit" loading={save.isPending} disabled={!value}>
                Salvar
              </Button>
            </ModalActions>
          </form>
        </Modal>
      )}
    </>
  );
}
